import React, { useState, useMemo, useEffect } from "react";
import { useSession } from '@supabase/auth-helpers-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';

import { getPracticeRecords } from '../api/supabaseAPI';
import { supabase } from '../supabaseClient';
import Loader from "../components/Loader";
import DiagnosisReport from "../components/DiagnosisReport";
import PracticeReportModal from "../components/PracticeReportModal";
import "../styles/Records.css";
import "../styles/Practice.css";
import "../styles/Layout.css";
import "../styles/Introduction.css";

// ✨ 1. 將 key 的名稱改為 localStorage，更清晰
const EXPANDED_ROW_LOCAL_KEY = 'theralingua_records_expanded_row';

// =================================================================
// ==   數據處理輔助函數 (Data Processing Helpers)                ==
// =================================================================

const safeJsonParse = (str) => {
  if (typeof str === 'string' && str.trim().startsWith('{')) {
    try {
      return JSON.parse(str);
    } catch (e) {
      return null;
    }
  }
  return null;
};

const getUniqueErrorPhonemesText = (practiceError) => {
  if (!practiceError || !Array.isArray(practiceError) || practiceError.length === 0) return 'N/A';
  const seen = new Set();
  const unique = [];
  for (const item of practiceError) {
    const phoneme = item?.phoneme;
    if (!phoneme || typeof phoneme !== 'string') continue;
    if (seen.has(phoneme)) continue;
    seen.add(phoneme);
    unique.push(phoneme);
  }
  return unique.length > 0 ? unique.join(', ') : 'N/A';
};

const formatDifficultyLevel = (level, t) => {
  if (!level) return t('recordsPage.levelLabels.unknown');

  const practiceLevelMap = {
    Kindergarten: 'kindergarten',
    PrimarySchool: 'primary_school',
    MiddleSchool: 'middle_school',
    HighSchool: 'high_school',
    University: 'university',
    kindergarten: 'kindergarten',
    primary_school: 'primary_school',
    middle_school: 'middle_school',
    high_school: 'high_school',
    university: 'university',
  };

  const mappedPracticeLevel = practiceLevelMap[level];
  if (mappedPracticeLevel) {
    return t(`practicePage.levels.${mappedPracticeLevel}`);
  }

  if (level === 'initial_test') return t('recordsPage.levelLabels.initialTest');
  if (level.startsWith('initial_test_')) {
    const parts = level.split('_');
    if (parts.length >= 3) {
      return t('recordsPage.levelLabels.initialTestWithProgress', { step: parts[2] });
    }
    return t('recordsPage.levelLabels.initialTest');
  }
  // Course練習：Course{id}_{index} -> "Course"
  if (level.startsWith('Course')) {
    return t('recordsPage.levelLabels.course');
  }
  return level.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// =================================================================
// ==   Records 組件                                              ==
// =================================================================

export default function Records() {
  const { t } = useTranslation();
  const session = useSession();
  const userId = session?.user?.id;
  const { data: practiceLanguage } = useQuery({
    queryKey: ['userLanguage', userId],
    queryFn: async () => {
      if (!userId) return 'en_us';
      const { data: settings } = await supabase
        .from('user_settings')
        .select('language')
        .eq('user_id', userId)
        .single();
      return settings?.language || 'en_us';
    },
    enabled: !!userId,
    staleTime: Infinity,
  });
  
const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });
  
  // Report Modal state
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  
  // ✨ 2. 【【【核心修正】】】
  //    從 localStorage 讀取初始值
  const [expandedRow, setExpandedRow] = useState(() => {
    try {
      const storedId = localStorage.getItem(EXPANDED_ROW_LOCAL_KEY); // <--- 從 localStorage 讀取
      return storedId ? JSON.parse(storedId) : null;
    } catch (error) {
      console.error("Failed to parse expandedRow from localStorage", error);
      return null;
    }
  });
  const { data: rawRecords, isLoading: isLoadingRecords, isError: isRecordsError } = useQuery({
    queryKey: ['practiceRecords', userId, practiceLanguage],
    queryFn: () => getPracticeRecords(userId, practiceLanguage),
    enabled: !!userId && !!practiceLanguage,
    refetchOnWindowFocus: false,
  });  
  const sortedRecords = useMemo(() => {
    if (!rawRecords) return [];
    const sorted = [...rawRecords].sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (sortConfig.key === "error_rate") {
        valA = parseFloat(valA);
        valB = parseFloat(valB);
      }
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [rawRecords, sortConfig]);

  // ✨ 3. 【【【核心修正】】】
  //    使用 useEffect 將變化寫入 localStorage
  useEffect(() => {
    try {
      if (expandedRow !== null) {
        localStorage.setItem(EXPANDED_ROW_LOCAL_KEY, JSON.stringify(expandedRow)); // <--- 寫入 localStorage
      } else {
        localStorage.removeItem(EXPANDED_ROW_LOCAL_KEY); // <--- 從 localStorage 移除
      }
    } catch (error) {
      console.error("Failed to set expandedRow in localStorage", error);
    }
  }, [expandedRow]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const getSortClassName = (key) => {
    if (sortConfig.key !== key) return "sortable";
return `sortable active ${sortConfig.direction}`;
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setIsReportModalOpen(true);
  };

  if (isLoadingRecords) {
    return <main className="main-content width-records"><h1 className="page-title">{t('recordsPage.title')}</h1><Loader /></main>;
  }
  if (isRecordsError) {
    return <main className="main-content width-records"><p>{t('recordsPage.errorLoadingRecords')}</p></main>;
  }
  
  const hasAnyData = rawRecords && rawRecords.length > 0;

  return (
    <main className="main-content width-records">
      <h1 className="page-title">{t('recordsPage.title')}</h1>
      {hasAnyData ? (
        <div className="records-section">
          <table className="records-table">
            <thead>
              <tr>
                <th className={getSortClassName("created_at")} onClick={() => handleSort("created_at")}>{t('recordsPage.table.date')}</th>
                <th>{t('recordsPage.table.targetSentence')}</th>
                <th>{t('recordsPage.table.difficulty')}</th>
                <th className={getSortClassName("error_rate")} onClick={() => handleSort("error_rate")}>{t('recordsPage.table.errorRate')}</th>
                <th>{t('recordsPage.table.errorPhonemes')}</th>
                <th>{t('recordsPage.table.details')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedRecords.map((record) => (
                <React.Fragment key={record.psid}>
                  <tr>
                    <td>{format(parseISO(record.created_at), 'yyyy-MM-dd HH:mm')}</td>
                    <td>
                      <span
                        className="records-cell-singleline"
                        title={record.target_content || ''}
                      >
                        {record.target_content || 'N/A'}
                      </span>
                    </td>
                    <td>{formatDifficultyLevel(record.diffi_level, t)}</td>
                    <td>{`${((record.error_rate || 0) * 100).toFixed(2)}%`}</td>
                    <td>
                      {(() => {
                        const text = getUniqueErrorPhonemesText(record.practice_error);
                        return (
                          <span
                            className="records-cell-singleline"
                            title={text === 'N/A' ? '' : text}
                          >
                            {text}
                          </span>
                        );
                      })()}
                    </td>
                    <td><button className="details-btn" onClick={() => setExpandedRow(expandedRow === record.psid ? null : record.psid)}>{expandedRow === record.psid ? t('recordsPage.table.hide') : t('recordsPage.table.details')}</button></td>
                  </tr>
{expandedRow === record.psid && (
                    <tr className="details-row">
                      <td colSpan="6">
                        <div className="details-content">
                          <DiagnosisReport 
                            result={record.full_log} 
                            nextAction={
                              record.full_report && Object.keys(record.full_report).length > 0 ? (
                                <button 
                                  className="practice-btn secondary"
                                  onClick={() => handleViewReport(record.full_report)}
                                >
                                  {t('recordsPage.viewReport') || 'View Report'}
                                </button>
                              ) : null
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p>{t('recordsPage.noRecordsMessage')}</p>
      )}
      
      <PracticeReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        report={selectedReport}
      />
    </main>
  );
}
